import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { smartBatchApi, type CollectionPhaseData } from '@/services/api/smartBatch'
import { Check, Clock, AlertCircle } from 'lucide-react'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

export function CollectionPhasePage() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<CollectionPhaseData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [selectedLessons, setSelectedLessons] = useState<Set<string>>(new Set())
  const [fullDayActive, setFullDayActive] = useState(false)
  const [fullDayDate, setFullDayDate] = useState<string | null>(null)
  const [timeLeft, setTimeLeft] = useState<string>('')

  useEffect(() => {
    if (!token) return
    smartBatchApi.getCollectionPhase(token)
      .then(setData)
      .catch((e: any) => setError(e?.detail || e?.message || 'Link ungültig oder abgelaufen'))
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => {
    if (!data?.deadline) return
    const interval = setInterval(() => {
      const diff = new Date(data.deadline!).getTime() - Date.now()
      if (diff <= 0) {
        setTimeLeft('Abgelaufen')
        clearInterval(interval)
        return
      }
      const mins = Math.floor(diff / 60000)
      const secs = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`)
    }, 1000)
    return () => clearInterval(interval)
  }, [data?.deadline])

  const toggleLesson = useCallback((lessonId: string) => {
    setFullDayActive(false)
    setFullDayDate(null)
    setSelectedLessons(prev => {
      const next = new Set(prev)
      if (next.has(lessonId)) next.delete(lessonId)
      else next.add(lessonId)
      return next
    })
  }, [])

  const handleFullDayToggle = useCallback((date: string) => {
    if (fullDayDate === date) {
      setFullDayActive(false)
      setFullDayDate(null)
      setSelectedLessons(new Set())
    } else {
      setFullDayActive(true)
      setFullDayDate(date)
      const dayLessons = data?.lessons.filter(l => l.date === date) || []
      setSelectedLessons(new Set(dayLessons.map(l => l.lesson_request_id)))
    }
  }, [data, fullDayDate])

  const handleSubmit = async () => {
    if (!token) return
    setSubmitting(true)
    setError(null)
    try {
      if (fullDayActive && fullDayDate) {
        await smartBatchApi.respondToCollection(token, {
          accept_full_day_definitive: true,
          target_date: fullDayDate,
        })
      } else {
        await smartBatchApi.respondToCollection(token, {
          lesson_interests: Array.from(selectedLessons).map(id => ({ lesson_request_id: id, status: 'interested' })),
        })
      }
      setSubmitted(true)
    } catch (e: any) {
      setError(e?.detail || 'Fehler beim Senden')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    if (!token) return
    setSubmitting(true)
    try {
      await smartBatchApi.respondToCollection(token, { lesson_interests: [] })
      setSubmitted(true)
    } catch (e: any) {
      setError(e?.detail || 'Fehler')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Laden...</div>

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{error}</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-gray-700 font-medium">Vielen Dank!</p>
          <p className="text-sm text-gray-500 mt-1">Ihre Antwort wurde gespeichert.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto p-4">
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-start">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{data?.requesting_teacher_name}</div>
              <div className="text-sm font-semibold text-gray-900">Verfügbarkeit melden</div>
              <div className="text-xs text-gray-500">{data?.date}</div>
            </div>
            {timeLeft && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs font-bold text-amber-700">
                <Clock className="w-3 h-3" /> {timeLeft}
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="text-xs text-gray-600 mb-3 p-2 bg-gray-50 rounded border-l-2 border-blue-400">
              Bitte markieren Sie die Lektionen, die Sie übernehmen können.
            </div>

            {data?.allow_definitive_full_day && data.available_full_day_dates?.map(date => (
              <button
                key={date}
                onClick={() => handleFullDayToggle(date)}
                className={`w-full flex items-center gap-2 p-3 border rounded mb-3 text-left transition-colors ${
                  fullDayDate === date ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${fullDayDate === date ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                  {fullDayDate === date && <Check className="w-3 h-3 text-white" />}
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-800">Ganzen Tag übernehmen ({date})</div>
                  <div className="text-xs text-gray-500">Definitive Zusage — kein weiteres Warten</div>
                </div>
              </button>
            ))}

            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Einzelne Lektionen</div>
            {data?.lessons && sortLessonsByDateTime(data.lessons).map((lesson) => (
              <button
                key={lesson.lesson_request_id}
                onClick={() => toggleLesson(lesson.lesson_request_id)}
                className={`w-full flex items-center gap-3 p-2.5 border rounded mb-1.5 text-left transition-colors ${
                  selectedLessons.has(lesson.lesson_request_id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${selectedLessons.has(lesson.lesson_request_id) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                  {selectedLessons.has(lesson.lesson_request_id) && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-800">{lesson.subject_name} · {lesson.klass_name}</div>
                  <div className="text-xs text-gray-500">{lesson.room_name}</div>
                </div>
                <div className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                  {lesson.start_time}–{lesson.end_time}
                </div>
              </button>
            ))}

            {error && <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{error}</div>}

            <div className="flex gap-2 mt-4">
              <button onClick={handleDecline} disabled={submitting} className="flex-1 px-3 py-2.5 border border-red-200 text-red-600 rounded text-xs font-semibold hover:bg-red-50 disabled:opacity-50">
                Ablehnen
              </button>
              <button onClick={handleSubmit} disabled={submitting || (selectedLessons.size === 0 && !fullDayActive)} className="flex-1 px-3 py-2.5 bg-blue-500 text-white rounded text-xs font-semibold hover:bg-blue-600 disabled:opacity-50">
                {submitting ? 'Senden...' : 'Verfügbarkeit melden'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
